import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Stars = ({ value, size = "w-4 h-4", onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} data-testid={onChange ? `star-${n}` : undefined} onClick={() => onChange?.(n)} className={onChange ? "cursor-pointer" : "cursor-default"}>
        <Star className={`${size} ${n <= Math.round(value || 0) ? "fill-gold text-gold" : "text-indigo-deep/20"}`} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ entity, reviews, avg, count }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState(null);
  const [f, setF] = useState({ rating: 5, title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!user) { setEligibility(null); return; }
    api.get(`/my/can-review/${entity.id}`).then((r) => setEligibility(r.data)).catch(() => setEligibility(null));
  }, [user, entity.id]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/reviews", { entity_id: entity.id, ...f });
      setSubmitted(true);
      toast.success(t("review_pending"));
    } catch (err) { toast.error(apiErrToStr(err)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="reviews-section" className="mt-8">
      <div className="flex items-end justify-between gap-4 mb-4">
        <h3 className="font-display text-xl text-indigo-deep">{t("reviews")}</h3>
        {count > 0 && (
          <div className="flex items-center gap-2 text-sm" data-testid="review-summary">
            <Stars value={avg} /><span className="font-semibold text-indigo-deep">{avg}</span><span className="text-indigo-deep/50">({count})</span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <p className="text-sm text-indigo-deep/60 bg-white rounded-lg p-4 border border-gold/15">{t("no_reviews")}</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-white rounded-lg p-4 border border-gold/15">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-indigo-deep text-sm">{r.user_name}</div>
                <Stars value={r.rating} size="w-3.5 h-3.5" />
              </div>
              {r.title && <div className="font-display text-indigo-deep mt-1">{r.title}</div>}
              {r.body && <p className="text-sm text-indigo-deep/75 mt-1">{r.body}</p>}
              <div className="text-[11px] text-indigo-deep/40 mt-2">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 bg-surfacealt/40 rounded-xl p-5 border border-gold/15">
        {!user ? (
          <Link to="/auth" data-testid="review-login-link" className="text-sm font-semibold text-gold-dark">{t("review_login")}</Link>
        ) : submitted || eligibility?.existing ? (
          <p data-testid="review-done" className="text-sm text-indigo-deep/70">{submitted ? t("review_pending") : t("review_done")}</p>
        ) : eligibility && !eligibility.has_booking ? (
          <p data-testid="review-need-booking" className="text-sm text-indigo-deep/60">{t("review_need_booking")}</p>
        ) : eligibility?.can_review ? (
          <div className="space-y-3">
            <div className="font-display text-lg text-indigo-deep">{t("write_review")}</div>
            <div><div className="text-xs uppercase tracking-wider text-indigo-deep/60 mb-1">{t("your_rating")}</div><Stars value={f.rating} size="w-6 h-6" onChange={(n) => setF({ ...f, rating: n })} /></div>
            <Input data-testid="review-title" placeholder={t("review_title")} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            <Textarea data-testid="review-body" rows={3} placeholder={t("review_body")} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
            <Button data-testid="review-submit" onClick={submit} disabled={busy} className="bg-indigo-deep text-white">{t("submit_review")}</Button>
          </div>
        ) : null}
      </div>
    </section>
  );
};
